import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Card,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { COLORS } from '../../shared/colors';

const CommonTable = ({ 
  columns, 
  data, 
  renderRow, 
  loading, 
  emptyMessage = "No data found", 
  currentPage,
  totalItems,
  limit,
  onPageChange,
  itemLabel = "User",
  tableContainerRef,
  tableContainerSx,
  cardSx,
  headerBg,
  preserveRowsOnLoading = false,
  showLoadingMore = false,
  loadingMoreMessage = "Loading more..."
}) => {
  const hasRows = Array.isArray(data) && data.length > 0;
  const tableHeaderBg = headerBg || COLORS.timesheet.summaryBg;
  const skeletonRows = 6;

  const renderSkeletonRows = () => (
    Array.from({ length: skeletonRows }).map((_, rowIndex) => (
      <TableRow key={`skeleton-row-${rowIndex}`}>
        {columns.map((column, columnIndex) => (
          <TableCell key={column.id || columnIndex} align={column.align}>
            <Skeleton
              variant={columnIndex === columns.length - 1 ? 'rounded' : 'text'}
              width={columnIndex === columns.length - 1 ? 72 : `${Math.max(45, 82 - columnIndex * 6)}%`}
              height={columnIndex === columns.length - 1 ? 28 : 24}
              animation="wave"
            />
          </TableCell>
        ))}
      </TableRow>
    ))
  );

  const renderLoadingMoreRow = () => (
    <TableRow>
      <TableCell colSpan={columns.length} align="center" sx={{ py: 2.5, borderBottom: 'none' }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <CircularProgress size={18} thickness={4} sx={{ color: COLORS.brand.primaryDark }} />
          <Typography variant="body2" sx={{ color: COLORS.brand.primaryDark, fontWeight: 500 }}>
            {loadingMoreMessage}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Card elevation={0} sx={{
        border: `1px solid ${COLORS.brand.primaryDark}`,
        borderColor: `${COLORS.brand.primaryDark} !important`,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        ...cardSx
    }}>
      {/* Table Header Wrapper - Completely isolated from scroll track */}
      <Box sx={{ borderBottom: `1px solid ${COLORS.brand.primaryDark}`, pr: '6px' }}>
        <Table sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            {columns.map((column, index) => (
              <col key={column.id || index} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow sx={{ 
              bgcolor: tableHeaderBg
            }}>
              {columns.map((column, index) => (
                <TableCell 
                  key={column.id || index} 
                  align={column.align}
                  sx={{ 
                    bgcolor: tableHeaderBg,
                    width: column.width, 
                    maxWidth: column.maxWidth, 
                    fontWeight: 600, 
                    color: COLORS.brand.primaryDark,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    borderBottom: 'none',
                    py: 2,
                    ...(index === 0 ? { pl: 3, pr: 3 } : {}),
                    ...(index === columns.length - 1 ? { pr: 3 } : {})
                  }}
                >
                  <Box
                    component="span"
                    title={typeof column.label === 'string' ? column.label : undefined}
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {column.label}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>
      </Box>

      {/* Table Body Wrapper - Houses native scrolling natively BELOW header */}
      <TableContainer ref={tableContainerRef} sx={{ 
        height: tableContainerSx?.height || tableContainerSx?.flex ? undefined : 'calc(100vh - 396px)', 
        overflow: 'auto',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(8, 43, 114, 0.2)', borderRadius: '3px' },
        ...tableContainerSx
      }}>
        <Table
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            },
            '& .MuiTableCell-root .MuiTypography-root': {
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            },
            '& .MuiTableCell-root .MuiChip-root': {
              maxWidth: '100%'
            },
            '& .MuiTableCell-root .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            },
            '& .MuiTableCell-root:first-of-type': { pl: 3, pr: 3 },
            '& .MuiTableCell-root:last-of-type': { pr: 3 }
          }}
        >
          <colgroup>
            {columns.map((column, index) => (
              <col key={column.id || index} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableBody>
            {loading && (!preserveRowsOnLoading || !hasRows) ? (
              renderSkeletonRows()
            ) : !hasRows ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: '2rem' }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {data.map((row, index) => renderRow(row, index))}
                {loading && preserveRowsOnLoading && showLoadingMore && hasRows && renderLoadingMoreRow()}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {totalItems > 0 && onPageChange && (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: '0.5rem', borderTop: `1px solid ${COLORS.brand.primaryDark}` }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.black }}>
                <Box 
                    component="span" 
                    sx={{ cursor: currentPage > 1 ? 'pointer' : 'default' }}
                    onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                >
                    &lt;
                </Box>
                {`Showing ${((currentPage - 1) * limit) + 1} - ${Math.min(currentPage * limit, totalItems)} of ${totalItems} ${itemLabel}`}
                <Box 
                    component="span" 
                    sx={{ cursor: currentPage * limit < totalItems ? 'pointer' : 'default' }}
                    onClick={() => currentPage * limit < totalItems && onPageChange(currentPage + 1)}
                >
                    &gt;
                </Box>
            </Typography>
          </Box>
      )}
    </Card>
  );
};

export default CommonTable;
